// Loyalty: the city's allegiance (value, state, the zone one Scholar hit would leave), threats on the way, and how it works.
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { art } from "@/kit/art";
import { Collapsible, Countdown, ScreenHero, TimeBar } from "@/kit/city";
import { IconSwords } from "@/kit/icons";
import { Button, Card, CardHead, Empty, Row, RowText, StatBand, cx } from "@/kit/primitives";
import { usePrompts } from "@/state/prompts";
import { useSession } from "@/state/session";
import { useRememberedOpen } from "@/state/collapse";
import { fmtCoords } from "@/game/format";
import { guildOf } from "@/game/guild";
import { fmtClock, fmtSpan } from "@/game/time";
import { WORLD } from "@/game/rules";
import { cityCoords, movements, paceLabel } from "@/game/city";
import { supportAskUrl } from "@/game/share";
import { TOOLS_URL, toolsHref, useVtOn } from "@/state/vt";
import { CityPage, useCity } from "./CityRoute";

const STATES: [number, string][] = [[100, "Steadfast"], [75, "Loyal"], [50, "Uneasy"], [25, "Wavering"], [0, "On the brink"]];
export const loyaltyState = (l: number) => STATES.find(([min]) => l >= min)?.[1] ?? "On the brink";

/** Hits are uniform integers hitMin..hitMax; the city falls when loyalty reaches 0 or below. Returns the fewest hits that
 * take it with ≥ 50% chance, and that chance (exact enumeration). */
export function takenIn(loyalty: number, hitMin = WORLD.scholar.hitMin, hitMax = WORLD.scholar.hitMax): { hits: number; chance: number } {
  const span = hitMax - hitMin + 1;
  let dist = new Map<number, number>([[0, 1]]); // sum → probability
  for (let k = 1; k <= 12; k++) {
    const next = new Map<number, number>();
    for (const [sum, p] of dist) for (let h = hitMin; h <= hitMax; h++) next.set(sum + h, (next.get(sum + h) ?? 0) + p / span);
    dist = next;
    let taken = 0;
    for (const [sum, p] of dist) if (sum >= loyalty) taken += p;
    if (taken >= 0.5) return { hits: k, chance: taken };
  }
  return { hits: 12, chance: 1 };
}

export function Loyalty() {
  const { v, base, wid } = useCity();
  const vtOn = useVtOn(wid);   // Valor Tools switched on for the player (Oct 15 2026): Request support opens it signed in
  const nav = useNavigate();
  const uid = useSession((s) => s.uid);
  // the guildmates a support request goes to (up to the mail's ten recipients), nearest the top of the list first
  const guildmates = useMemo(() => (guildOf(v)?.members ?? []).filter((m) => m.user_id !== uid).map((m) => m.login).slice(0, 10), [v, uid]);
  const [how, toggleHow] = useRememberedOpen("loyalty.how");
  const mv = useMemo(() => (v ? movements(v) : { incoming: [], outgoing: [] }), [v]);
  if (!v) return <CityPage hud={false}>{null}</CityPage>;
  const l = Math.max(0, Math.min(WORLD.scholar.max, v.loyalty ?? 0));
  const state = loyaltyState(l);
  const from = cityCoords(v);
  const threats = mv.incoming.filter((m) => m.kind === "attack");
  const t = takenIn(l);
  const lo = Math.max(0, l - WORLD.scholar.hitMax);
  const hi = Math.max(0, l - WORLD.scholar.hitMin);
  const fullIn = (WORLD.scholar.max - l) / WORLD.scholar.regainPerHour;
  const danger = l < 75 || threats.length > 0;
  return (
    <CityPage hud={false}>
      <div className="stack">
        <ScreenHero title="Loyalty" art={art.hero.loyalty} artWidth="58%" onBack={() => nav(base)} blurb={<>The people of <b className="gold">{v.village?.city_name}</b> and their faith in your reign</>} />
        <Card>
          <CardHead title="Allegiance" aside={<span className={cx(l < 50 ? "crimtxt" : l < 100 ? "gold" : "greentxt")} style={{ font: "700 var(--fs-label)/1 var(--caps)", letterSpacing: ".16em", textTransform: "uppercase" }}>{state}</span>} />
          <div className="loy-value"><b>{l}</b> <span className="muted">/ {WORLD.scholar.max}</span></div>
          <div className="loy-bar" role="progressbar" aria-valuenow={l} aria-valuemin={0} aria-valuemax={100}>
            <i className="fill" style={{ width: `${l}%` }} />
            {l > 0 && <i className="zone" style={{ left: `${lo}%`, width: `${Math.max(0, hi - lo)}%` }} />}
            <i className="tick" style={{ left: "25%" }} /><i className="tick" style={{ left: "50%" }} /><i className="tick" style={{ left: "75%" }} />
          </div>
          <div className="c-note left" style={{ color: "var(--crim-ink)" }}>{l > 0 ? `One Scholar hit leaves ${lo}–${hi}` : "The city has fallen."}</div>
          <StatBand stats={[
            { label: "Regains", value: `+${WORLD.scholar.regainPerHour}`, small: "/ hour" },
            { label: "Full in", value: l >= 100 ? "Full" : fmtSpan(fullIn * 3600_000), tone: l >= 100 ? ("green" as const) : undefined },
            { label: "Taken in", value: `${t.hits} hit${t.hits === 1 ? "" : "s"}`, tone: danger ? ("crim" as const) : undefined, small: `${Math.round(t.chance * 100)}% chance` },
          ]} />
        </Card>

        <Card>
          <CardHead title="Threats" badge={threats.length || undefined} aside={threats.length ? `${threats.length} incoming` : "none incoming"} />
          {!threats.length ? <Empty>No attacks on their way to {v.village?.city_name}.</Empty> : (
            <div className="rows">
              {threats.map((e) => {
                const pace = paceLabel(e, from);
                const scholarPace = /Scholar/.test(pace);
                return (
                  <Row key={String(e.m.id)} className="alert" style={{ flexWrap: "wrap" }}>
                    <span className="mv-disc crim"><IconSwords /></span>
                    <RowText line={<>Attack from <b className="crimtxt">{e.other.name}</b> {fmtCoords(e.other.coords)}</>} meta={<>Lands {fmtClock(e.endMs, true)}{pace ? ` · ${pace}` : ""} {scholarPace && <span className="risktag">Loyalty at risk</span>}</>} />
                    <div className="right"><div className="val crimtxt" style={{ font: "600 var(--fs-value)/1 var(--text)" }}><Countdown endMs={e.endMs} done="Landed" /></div></div>
                    <div className="row-body"><TimeBar startMs={e.startMs} endMs={e.endMs} color="crim" /></div>
                  </Row>
                );
              })}
            </div>
          )}
          <div className="c-grid2" style={{ marginTop: "calc(var(--u) * 14)" }}>
            {/* Ben, Oct 13 2026: Support opens the request in Valor Tools' Guild › Support, filled in (city, coordinates, landing
                by the first attack, what's coming), where the player picks the troops and chooses whether to share it to Discord */}
            <Button kind="crimson" onClick={() => { if (from) { const u = supportAskUrl(wid, { name: v.village?.city_name ?? "", coords: from }, threats, from); window.open(vtOn && u.startsWith(TOOLS_URL + "/") ? toolsHref(wid, u.slice(TOOLS_URL.length), true) : u, "_blank", "noopener"); } else usePrompts.getState().toast("This city's coordinates aren't known yet."); }}>Request support</Button>
            <Button kind="steel" onClick={() => nav(`${base}/rally/movement`)}>Rally Point</Button>
          </div>
          <div className="c-note">Opens Valor Tools' Guild › Support with the request filled in; you can share it to your guild's Discord from there.{" "}
            {guildmates.length > 0 && <button type="button" className="link" onClick={() => nav(`/w/${wid}/mail/new?to=${encodeURIComponent(guildmates.join(", "))}&subject=${encodeURIComponent(`Support needed in ${v.village.city_name} ${fmtCoords(v.village.coords)}`)}`)}>Or write to your guild</button>}
          </div>
        </Card>

        <Card>
          <CardHead title="Recent" aside="last 24 hours" />
          <Empty>Loyalty changes are written into your battle reports. <button type="button" className="link" onClick={() => nav(`/w/${wid}/reports?f=battles`)}>Open the reports</button></Empty>
        </Card>

        <Collapsible title="How loyalty works" aside="Scholars · regain · capture" open={how} onToggle={toggleHow}>
          <ul className="howlist">
            <li>Every Scholar that survives an attack takes <b>{WORLD.scholar.hitMin}–{WORLD.scholar.hitMax}</b> loyalty from the city (+{WORLD.scholar.prestigeBonus} with Prestige).</li>
            <li>Loyalty regains <b>+{WORLD.scholar.regainPerHour} every hour</b> up to {WORLD.scholar.max}.</li>
            <li>At <b>0 or below</b> the city changes hands and starts at {WORLD.scholar.onConquest} loyalty for its new lord.</li>
          </ul>
        </Collapsible>
      </div>
    </CityPage>
  );
}

